import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Textbox from 'carbon-react/lib/components/textbox';
import { MultiSelect, Option } from 'carbon-react/lib/components/select';

import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';
import { DRIVER_GROUP_FORM_FIELD_IDS } from './constants';
import { NonFinancialDriverTreeNode } from '../../../types';

export interface DriverGroupFormScreenProps {
  error?: string;
  validationErrors?: Record<string, { id: string; message: string }>;
  groupName: string;
  onGroupNameChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  onBlur?: () => void;
  isEditMode: boolean;
  availableDrivers: NonFinancialDriverTreeNode[];
  selectedDrivers: NonFinancialDriverTreeNode[];
  setSelectedDrivers: (drivers: NonFinancialDriverTreeNode[]) => void;
  revalidate: () => void;
}

export const DriverGroupFormScreen = ({
  error,
  validationErrors = {},
  groupName,
  onGroupNameChange,
  onClose,
  onSubmit,
  onBlur,
  isEditMode,
  availableDrivers,
  selectedDrivers,
  setSelectedDrivers,
  revalidate
}: DriverGroupFormScreenProps) => {
  const intl = useIntl();

  const handleDriversChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!availableDrivers) {
      return;
    }

    const newDriverIds = (event.target.value as unknown) as string[];
    const idSet = new Set(newDriverIds);
    const filteredDrivers = availableDrivers.filter(driver => idSet.has(driver.id));

    setSelectedDrivers(filteredDrivers);
  };

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={onSubmit}
      createText={
        isEditMode ? (
          <FormattedMessage id="common.save" defaultMessage="Save" />
        ) : (
          <FormattedMessage id="common.create" defaultMessage="Create" />
        )
      }
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Box mb={3}>
          <Textbox
            label={intl.formatMessage({
              id: 'consolidation.group_name',
              defaultMessage: 'Group name'
            })}
            name={DRIVER_GROUP_FORM_FIELD_IDS.GROUP_NAME}
            id={DRIVER_GROUP_FORM_FIELD_IDS.GROUP_NAME}
            value={groupName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onGroupNameChange(e.target.value)}
            onBlur={onBlur}
            error={validationErrors.name?.message}
            required
          />
        </Box>
        <Box mb={3}>
          <MultiSelect
            required
            label={intl.formatMessage({
              id: 'predict.select_drivers_to_add_to_group',
              defaultMessage: 'Select drivers to add to group'
            })}
            name={DRIVER_GROUP_FORM_FIELD_IDS.DRIVERS}
            id={DRIVER_GROUP_FORM_FIELD_IDS.DRIVERS}
            value={selectedDrivers.map(driver => driver.id)}
            onChange={handleDriversChange}
            onBlur={revalidate}
            error={validationErrors.drivers?.message}
          >
            {availableDrivers.map(driver => (
              <Option key={driver.id} text={driver.name} value={driver.id} />
            ))}
          </MultiSelect>
        </Box>
      </>
    </WizardScreen>
  );
};
